// Injected logger for the next OGS render path — a level the bundle declares, not a build constant.
// Notes: ~/comments/aos-threejs-poc/ogsLog_createOgsLogger_levels.md

/** Canonical levels. `error` is the floor: a failure is never suppressible. */
export const LOG_LEVELS = ["error", "warn", "info", "verbose"] as const;

export type LogLevel = (typeof LOG_LEVELS)[number];

/** Historical spelling of `error`, accepted so a bundle writing "off" still means "failures only". */
export const LOG_LEVEL_ALIASES: Readonly<Record<string, LogLevel>> = Object.freeze({ off: "error" });

export const DEFAULT_LOG_LEVEL: LogLevel = "warn";

const RANK: Readonly<Record<LogLevel, number>> = Object.freeze({ error: 0, warn: 1, info: 2, verbose: 3 });

/** The level a raw value names, or null — never a silent fallback. */
export function asLogLevel(value: unknown): LogLevel | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  if ((LOG_LEVELS as readonly string[]).includes(key)) return key as LogLevel;
  return LOG_LEVEL_ALIASES[key] ?? null;
}

export interface LogSink {
  error(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  info(...args: unknown[]): void;
  debug(...args: unknown[]): void;
}

export interface OgsLogger extends LogSink {
  readonly level: LogLevel;
  readonly prefix: string;
  /** A logger tagged `[<prefix>:<scope>]`, sharing this one's level and sink. */
  child(scope: string): OgsLogger;
}

export interface OgsLoggerOptions {
  level?: LogLevel;
  prefix?: string;
  sink?: LogSink;
}

/** A logger whose level gates info and debug only. */
export function createOgsLogger({ level = DEFAULT_LOG_LEVEL, prefix = "ogs", sink = console }: OgsLoggerOptions = {}): OgsLogger {
  const tag = `[${prefix}]`;
  const rank = RANK[level] ?? RANK[DEFAULT_LOG_LEVEL];
  return {
    level,
    prefix,
    error: (...args) => sink.error(tag, ...args),
    warn: (...args) => sink.warn(tag, ...args),
    info: (...args) => { if (rank >= RANK.info) sink.info(tag, ...args); },
    debug: (...args) => { if (rank >= RANK.verbose) sink.debug(tag, ...args); },
    child: (scope) => createOgsLogger({ level, prefix: `${prefix}:${scope}`, sink }),
  };
}

/** Test seam: records nothing, so a unit test asserts returned reports instead of console text. */
export function createSilentLogger(level: LogLevel = "error"): OgsLogger {
  const noop = () => {};
  return createOgsLogger({ level, sink: { error: noop, warn: noop, info: noop, debug: noop } });
}
